import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const THEME_STORAGE_KEY = "cms-theme-mode";
const DEFAULT_THEME_MODE = "light";

const CmsColorModeContext = createContext({
  mode: DEFAULT_THEME_MODE,
  setMode: () => {},
  toggleColorMode: () => {},
});

const resolveInitialMode = () => {
  const storedMode = localStorage.getItem(THEME_STORAGE_KEY);
  if (storedMode === "light" || storedMode === "dark") {
    return storedMode;
  }

  const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  return prefersDark ? "dark" : DEFAULT_THEME_MODE;
};

const CmsColorModeProvider = ({ children }) => {
  const [mode, setMode] = useState(resolveInitialMode);

  useEffect(() => {
    document.documentElement.setAttribute("data-cms-theme", mode);
    document.body.setAttribute("data-cms-theme", mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,
      setMode: (nextMode) => {
        if (nextMode === "light" || nextMode === "dark") {
          setMode(nextMode);
        }
      },
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "dark" ? "light" : "dark"));
      },
    }),
    [mode]
  );

  return (
    <CmsColorModeContext.Provider value={value}>
      {children}
    </CmsColorModeContext.Provider>
  );
};

const useCmsColorMode = () => useContext(CmsColorModeContext);

export { CmsColorModeProvider, useCmsColorMode };
